import React, { useState, useEffect } from 'react';
import { Folder, Eye, Target } from 'lucide-react';

const CategoryFormModal = ({ category, onClose, onSave }) => {
    const [formData, setFormData] = useState({
      name: category?.name || '',
      description: category?.description || '',
      icon: category?.icon || <Folder />,
      color: category?.color || 'blue',
      is_visible: category?.is_visible ?? true,
      specialization_required: category?.specialization_required ?? false
    });
  
    const [saving, setSaving] = useState(false);
  
    const colors = [
      'blue', 'green', 'purple', 'red', 'yellow', 'indigo', 'pink', 'gray',
      'orange', 'teal', 'cyan', 'emerald', 'violet', 'rose', 'amber', 'lime'
    ];
  
    const commonIcons = [
      '📁', '🏢', '💵', '🚗', '⚡', '👨‍👩‍👧‍👦', '🛂', '⚖️', '🏦', '🗠',
      '🛒', '🏥', '🎓', '🌱', '💻', '💧', '🔧', '📋', '🎯', '🌟'
    ];
  
    const handleSubmit = async (e) => {
      e.preventDefault();
  
      if (!formData.name.trim()) {
        success('El nombre es requerido');
        return;
      }
  
      setSaving(true);
      try {
        await onSave(formData);
      } finally {
        setSaving(false);
      }
    };
  
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1200]">
        <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[620px] overflow-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">
                {category ? 'Editar Categoría' : 'Nueva Categoría'}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>
          </div>
  
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Información básica */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre de la Categoría *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ej: Inmobiliarios y Urbanos"
                  required
                />
              </div>
            </div>
  
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descripción
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-24"
                placeholder="Describe los servicios incluidos en esta categoría..."
              />
            </div>
  
            {/* Personalización visual */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Icono
                </label>
                <div className="grid grid-cols-8 gap-2 mb-3">
                  {commonIcons.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon })}
                      className={`p-2 text-2xl rounded-lg border transition-colors ${formData.icon === icon
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg text-center"
                  placeholder="O escribe emoji personalizado"
                />
              </div>
  
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Color del Tema
                </label>
                <div className="grid grid-cols-8 gap-2">
                  {colors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${formData.color === color
                        ? 'border-gray-800 scale-110'
                        : 'border-gray-300'
                        }`}
                      style={{
                        backgroundColor: color === 'gray' ? '#6B7280' :
                          color === 'blue' ? '#3B82F6' :
                            color === 'green' ? '#10B981' :
                              color === 'purple' ? '#8B5CF6' :
                                color === 'red' ? '#EF4444' :
                                  color === 'yellow' ? '#F59E0B' :
                                    color === 'indigo' ? '#6366F1' :
                                      color === 'pink' ? '#EC4899' : '#6B7280'
                      }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            </div>
  
            {/* Configuración avanzada */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_visible}
                    onChange={(e) => setFormData({ ...formData, is_visible: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 flex row items-center gap-2"><Eye /> Visible para clientes</span>
                </label>
  
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.specialization_required}
                    onChange={(e) => setFormData({ ...formData, specialization_required: e.target.checked })}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 flex row items-center gap-2"><Target /> Requiere gestor especializado</span>
                </label>
              </div>
            </div>
  
            {/* Botones */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="px-6 py-3 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving || !formData.name.trim()}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Guardando...' : category ? 'Actualizar' : 'Crear'} Categoría
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

export default CategoryFormModal;